import { ListFilter } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { WorkWorker } from './types'

function greetingForHour(hour: number): string {
  if (hour >= 5 && hour < 12) return 'Bom dia'
  if (hour >= 12 && hour < 18) return 'Boa tarde'
  return 'Boa noite'
}

type Period = 'manha' | 'tarde' | 'noite'

function periodOf(hour: number): Period {
  if (hour >= 5 && hour < 12) return 'manha'
  if (hour >= 12 && hour < 18) return 'tarde'
  return 'noite'
}

const WEEKDAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']
const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
const PERIOD_SUFFIX: Record<Period, string> = { manha: 'de manhã', tarde: 'à tarde', noite: 'à noite' }

// Quips por período — o "gás" que combina com a hora.
const PERIOD_QUIPS: Record<Period, string[]> = {
  manha: ['hora de mandar ver', 'bora começar com tudo', 'café na mão, foco no jogo'],
  tarde: ['mantendo o ritmo', 'segue firme, sem afrouxar', 'bora manter o embalo'],
  noite: ['vamos dar aquele gás', 'fechando o dia com capricho', 'no gás até o fim'],
}

// Toques por dia da semana — cada dia com sua vibe (sem repetir o nome do dia).
const DAY_QUIPS: Record<number, string[]> = {
  0: ['adiantando a semana', 'fim de semana e no jogo'],
  1: ['semana nova, energia nova', 'pra cima desde já'],
  2: ['no embalo da semana', 'seguindo o fluxo'],
  3: ['metade do caminho, segue firme', 'no meio do furacão'],
  4: ['quase sexta, foco total', 'a reta final tá logo ali'],
  5: ['quem fez, fez', 'sextou, mas ainda tem trampo', 'bora fechar a semana'],
  6: ['a máquina não para', 'quem tá aqui é raiz'],
}

// Frase de trabalho contextual (dia da semana + período), estável no dia mas
// mudando dia a dia. Parte dos dias usa a variante com a data (estilo pedido).
function workLine(now: Date): string {
  const seed = Math.floor(now.getTime() / 86_400_000)
  const weekday = WEEKDAYS[now.getDay()]

  if (seed % 3 === 0) {
    const day = now.getDate()
    const month = MONTHS[now.getMonth()]
    const dateVariants = [
      `Dia ${day} de ${month}, vamos trabalhar?`,
      `Dia ${day} de ${month}, bora fazer acontecer?`,
      `${weekday}, ${day} de ${month} — vamos nessa?`,
    ]
    // seed já é múltiplo de 3 aqui, então seed%3 seria sempre 0 — divide antes
    // pra as três variantes de data realmente alternarem.
    return dateVariants[Math.floor(seed / 3) % dateVariants.length]
  }

  const period = periodOf(now.getHours())
  const quips = [...(DAY_QUIPS[now.getDay()] ?? []), ...PERIOD_QUIPS[period]]
  return `${weekday} ${PERIOD_SUFFIX[period]}, ${quips[seed % quips.length]}.`
}

interface Props {
  userName: string | null
  workerCount: number
  workers: WorkWorker[]
  filterWorkerId: string
  onFilterChange: (workerId: string) => void
}

/**
 * Cabeçalho da home de gestão. A saudação por hora local (19px) + uma frase de
 * trabalho contextual (dia da semana + período, com variantes bem-humoradas e a
 * variante com data) no lugar do panorama seco de contagens — e, à direita, o
 * filtro por agente que recorta a home inteira. Conversar segue na sidebar/⌘N.
 */
export function WorkGreeting({ userName, workerCount, workers, filterWorkerId, onFilterChange }: Props) {
  const firstName = (userName || '').trim().split(/\s+/)[0] || null
  const now = new Date()
  const greeting = greetingForHour(now.getHours())

  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h1 className="text-[19px] font-semibold leading-tight tracking-[-0.015em] text-foreground">
        {firstName ? `${greeting}, ${firstName}.` : `${greeting}.`}
      </h1>
      <p className="text-[13px] text-muted-foreground">{workLine(now)}</p>
      {workerCount > 0 && (
        <div className="ml-auto self-center">
          <Select value={filterWorkerId} onValueChange={onFilterChange}>
            <SelectTrigger className="h-8 w-[188px] text-[13px]" aria-label="Filtrar por agente">
              <ListFilter className="size-3.5 text-muted-foreground" aria-hidden />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os agentes</SelectItem>
              {workers.map((worker) => (
                <SelectItem key={worker.id} value={worker.id}>{worker.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  )
}
